import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createRole,
  deleteRole,
  fetchRoles,
  toggleRoleStatus,
  updateRole,
} from "../services/api";
import { setAlert } from "../store/slices/alertSlice";
import { Modal, Pagination } from "react-bootstrap";
import MasterListToolbar from "./MasterListToolbar";
import { useDebouncedSearch } from "../hooks/useDebouncedSearch";

const PAGE_SIZE = 10;

const emptyForm = {
  roleName: "",
  permissions: "",
  isActive: true,
};

const mapRoleToForm = (role) => ({
  roleName: role.roleName || "",
  permissions: (role.permissions || []).join(", "),
  isActive: role.isActive !== false,
});

const RoleManagement = () => {
  const dispatch = useDispatch();
  const [roles, setRoles] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteRoleId, setDeleteRoleId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRoles, setTotalRoles] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { searchInput, setSearchInput, appliedSearch, clearSearch } = useDebouncedSearch();

  const loadRoles = useCallback(async (page, search) => {
    setLoading(true);
    try {
      const response = await fetchRoles(page, PAGE_SIZE, search);
      setRoles(response.data.roles || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalRoles(response.data.totalRoles || 0);
      setCurrentPage(Number(response.data.currentPage || page));
    } catch (error) {
      dispatch(setAlert({ type: "danger", message: "Error loading roles" }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadRoles(currentPage, appliedSearch);
  }, [currentPage, appliedSearch, loadRoles]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedSearch]);

  const handleClearSearch = () => {
    clearSearch();
    setCurrentPage(1);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setFormData(emptyForm);
    setEditingRoleId(null);
  };

  const openAddModal = () => {
    setFormData(emptyForm);
    setEditingRoleId(null);
    setShowFormModal(true);
  };

  const openEditModal = (role) => {
    setFormData(mapRoleToForm(role));
    setEditingRoleId(role._id);
    setShowFormModal(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.roleName.trim()) {
      dispatch(setAlert({ type: "danger", message: "Role name is required" }));
      return;
    }

    const payload = {
      roleName: formData.roleName.trim(),
      permissions: formData.permissions
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    };

    if (editingRoleId) {
      payload.isActive = formData.isActive;
    }

    setSaving(true);
    try {
      if (editingRoleId) {
        await updateRole(editingRoleId, payload);
        dispatch(setAlert({ type: "success", message: "Role updated successfully" }));
      } else {
        await createRole(payload);
        dispatch(setAlert({ type: "success", message: "Role created successfully" }));
      }

      closeFormModal();
      loadRoles(currentPage, appliedSearch);
    } catch (error) {
      dispatch(setAlert({
        type: "danger",
        message: error.response?.data?.error || "Unable to save role",
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteRole(deleteRoleId);
      dispatch(setAlert({ type: "success", message: "Role deleted successfully" }));
      setShowDeleteModal(false);
      loadRoles(currentPage, appliedSearch);
    } catch (error) {
      dispatch(setAlert({
        type: "danger",
        message: error.response?.data?.error || "Unable to delete role",
      }));
    }
  };

  const handleToggleStatus = async (role) => {
    const nextStatus = role.isActive === false;
    try {
      await toggleRoleStatus(role._id, nextStatus);
      dispatch(setAlert({
        type: "success",
        message: nextStatus ? "Role enabled successfully" : "Role disabled successfully",
      }));
      loadRoles(currentPage, appliedSearch);
    } catch (error) {
      dispatch(setAlert({
        type: "danger",
        message: error.response?.data?.error || "Error updating role status",
      }));
    }
  };

  const getStatusBadge = (role) => {
    if (role.isActive === false) {
      return <span className="badge bg-secondary">Disabled</span>;
    }
    return <span className="badge bg-success">Active</span>;
  };

  const pageStart = totalRoles === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(currentPage * PAGE_SIZE, totalRoles);

  return (
    <div className="container-fluid py-4">
      <div className="card shadow-sm">
        <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <h4 className="mb-0">Role Management</h4>
            <small className="text-muted">
              {totalRoles} role{totalRoles === 1 ? "" : "s"} total
              {appliedSearch ? ` matching "${appliedSearch}"` : ""}
            </small>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2 ms-auto">
            <MasterListToolbar
              searchValue={searchInput}
              onSearchChange={(event) => setSearchInput(event.target.value)}
              searchPlaceholder="Search role name or permission..."
              searchAriaLabel="Search roles"
              onClear={handleClearSearch}
              showClear={Boolean(appliedSearch)}
            />
            <button type="button" className="btn btn-primary btn-sm" onClick={openAddModal}>
              <i className="bi bi-plus-lg me-1" aria-hidden="true"></i>
              Add Role
            </button>
          </div>
        </div>

        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading</span>
              </div>
            </div>
          ) : roles.length === 0 ? (
            <div className="text-center text-muted py-5">
              {appliedSearch
                ? <>No roles found for <strong>{appliedSearch}</strong>.</>
                : <>No roles found. Click <strong>Add Role</strong> to create one.</>}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Role Name</th>
                    <th>Permissions</th>
                    <th>Status</th>
                    <th className="text-end" style={{ width: "180px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map((role) => (
                    <tr key={role._id}>
                      <td className="fw-semibold">{role.roleName}</td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {(role.permissions || []).length ? role.permissions.map((permission) => (
                            <span className="badge bg-light text-dark border" key={permission}>
                              {permission}
                            </span>
                          )) : <span className="text-muted small">No permissions</span>}
                        </div>
                      </td>
                      <td>{getStatusBadge(role)}</td>
                      <td className="text-end">
                        <button
                          className="btn btn-outline-primary btn-sm me-1"
                          title="Edit Role"
                          type="button"
                          onClick={() => openEditModal(role)}
                        >
                          <i className="bi bi-pencil" aria-hidden="true"></i>
                        </button>
                        <button
                          className={`btn btn-sm me-1 ${role.isActive === false ? "btn-outline-success" : "btn-outline-secondary"}`}
                          title={role.isActive === false ? "Enable Role" : "Disable Role"}
                          type="button"
                          onClick={() => handleToggleStatus(role)}
                        >
                          <i
                            className={`bi ${role.isActive === false ? "bi-check-circle" : "bi-slash-circle"}`}
                            aria-hidden="true"
                          ></i>
                        </button>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          title="Delete Role"
                          type="button"
                          onClick={() => {
                            setDeleteRoleId(role._id);
                            setShowDeleteModal(true);
                          }}
                        >
                          <i className="bi bi-trash" aria-hidden="true"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
              <small className="text-muted">
                Showing {pageStart}-{pageEnd} of {totalRoles}
              </small>
              <Pagination className="mb-0">
                <Pagination.Prev
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                />
                {[...Array(totalPages)].map((_, index) => (
                  <Pagination.Item
                    key={index}
                    active={index + 1 === currentPage}
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                />
              </Pagination>
            </div>
          )}
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{editingRoleId ? "Edit Role" : "Add Role"}</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit} noValidate>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">
                Role Name <span className="text-danger">*</span>
              </label>
              <input
                className="form-control"
                value={formData.roleName}
                onChange={(event) => setFormData((prev) => ({ ...prev, roleName: event.target.value }))}
                placeholder="Quality Manager"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Permissions</label>
              <textarea
                className="form-control"
                rows="4"
                value={formData.permissions}
                onChange={(event) => setFormData((prev) => ({ ...prev, permissions: event.target.value }))}
                placeholder="Comma separated permissions"
              />
            </div>
            {editingRoleId && (
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="role-is-active"
                  checked={formData.isActive}
                  onChange={(event) => setFormData((prev) => ({ ...prev, isActive: event.target.checked }))}
                />
                <label className="form-check-label" htmlFor="role-is-active">
                  Role active (available for assignment)
                </label>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-outline-secondary" onClick={closeFormModal}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : editingRoleId ? "Update Role" : "Create Role"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will soft-delete the role using <strong>isDeleted</strong>. The record will be
          deactivated (<strong>isActive = false</strong>), hidden from this list, and
          <strong> deletedBy</strong> / <strong>deletedAt</strong> will be recorded.
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={handleDelete}>
            Delete
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RoleManagement;
